import { useQuery, useSuspenseQuery } from "@tanstack/react-query";

import { ApiClient } from "../api-client";

import type { GetStudentCoursesResponse } from "../generated-api";
import type { SupportedLanguages } from "@repo/shared";
import type { SortOption } from "~/types/sorting";

type CourseParams = {
  /** Filter by course title only */
  title?: string;
  /** Filter by course description only */
  description?: string;
  /** Search across both title AND description fields simultaneously */
  searchQuery?: string;
  category?: string;
  sort?: SortOption;
  userId?: string;
  language?: SupportedLanguages;
};

type QueryOptions = {
  enabled?: boolean;
};

export const studentCoursesQueryOptions = (
  searchParams?: CourseParams,
  options: QueryOptions = { enabled: true },
) => ({
  queryKey: ["get-student-courses", searchParams],
  /**
   * Al ENTRAR a Mi Aula siempre se vuelve a pedir.
   *
   * El avance dentro de una clase —lo recorrido de un video, las preguntas contestadas— se
   * guarda con un latido cada pocos segundos y no invalida nada: hacerlo sería tirar abajo la
   * caché del curso entero varias veces por minuto. La contra era que el alumno salía de la
   * clase, volvía a Mi Aula por el menú, y con `staleTime` de 5 minutos veía el porcentaje de
   * cuando había entrado.
   *
   * Pedirlo de nuevo al montar es lo barato y correcto: pasa una vez por visita, no mientras
   * el alumno cursa.
   */
  refetchOnMount: "always" as const,
  queryFn: async () => {
    const response = await ApiClient.api.courseControllerGetStudentCourses({
      page: 1,
      perPage: 100,
      ...(searchParams?.title && { title: searchParams.title }),
      ...(searchParams?.description && { description: searchParams.description }),
      ...(searchParams?.searchQuery && { searchQuery: searchParams.searchQuery }),
      ...(searchParams?.category && { category: searchParams.category }),
      ...(searchParams?.sort && { sort: searchParams.sort }),
      ...(searchParams?.userId && { userId: searchParams.userId }),
      ...(searchParams?.language && { language: searchParams.language }),
    });
    return response.data;
  },
  select: (data: GetStudentCoursesResponse) => data.data,
  ...options,
});

export function useStudentCourses(searchParams?: CourseParams) {
  return useQuery(studentCoursesQueryOptions(searchParams));
}

export function useStudentCoursesSuspense(searchParams?: CourseParams) {
  return useSuspenseQuery(studentCoursesQueryOptions(searchParams));
}
